import React, { useEffect, useState } from "react";
import { operationalColumns } from "../../../../constants/bussiness";
import BasicCustomTable from "../../../../component/basicTable";
import axiosInstance from "../../../../configuration/request";
import { useSnackbar } from "../../../../component/SnackbarContext";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isSameOrAfter);

const daysOfWeek = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

const defaultStoreHoursObject = daysOfWeek.reduce((acc, day) => {
  acc[day] = { open: null, close: null, note: "" };
  return acc;
}, {});

const defaultStoreHours = daysOfWeek.map((day) => ({
  day,
  open_time: null,
  close_time: null,
  order_status: "Closed",
  note: "",
}));

export default function WeeklyShedule() {
  const [storeHours, setStoreHours] = useState(defaultStoreHours);
  const [initialStoreHours, setInitialStoreHours] = useState(defaultStoreHoursObject);
  const showSnackbar = useSnackbar();
  const { loginDetails } = useSelector((state) => state.counter);
  const storeId = loginDetails.storeId || null;

  useEffect(() => {
    fetchStore();
  }, []);

  const fetchStore = () => {
    axiosInstance
      .get(`/customer/store/get-store?id=${storeId}`)
      .then((res) => {
        const store_hr = res?.data?.store_hours;
        if (store_hr && typeof store_hr === "object" && Object.keys(store_hr).length > 0) {
          const storeHoursMapped = daysOfWeek.map((day) => {
            const times = store_hr[day] || {};
            return {
              day,
              open_time: times.open ? dayjs(times.open, "hh:mm A") : null,
              close_time: times.close ? dayjs(times.close, "hh:mm A") : null,
              order_status: times.open && times.close ? "Open" : "Closed",
              note: times.note || "",
            };
          });
          setStoreHours(storeHoursMapped);
          setInitialStoreHours(store_hr);
        } else {
          setStoreHours(defaultStoreHours);
          setInitialStoreHours(defaultStoreHoursObject);
        }
      })
      .catch(() => {
        showSnackbar("Something went wrong!", "error");
      });
  };

  const handleTimeChange = (row, field, value) => {
    setStoreHours((prev) =>
      prev.map((item) =>
        item.day === row.day ? { ...item, [field]: value } : item
      )
    );
  };

  const onAccept = (row, field, value) => {
    const updatedRow = storeHours.find((item) => item.day === row.day);
    const otherField = field === "open_time" ? "close_time" : "open_time";
    const otherTime = updatedRow[otherField];
    const newValue = dayjs(value);

    // If the other time isn't set, update local state only and skip validation
    if (!otherTime || !dayjs(otherTime).isValid()) {
      return; 
    }

    const newOther = dayjs(otherTime);

    // Validate only when both times are present
    if (
      (field === "open_time" && !newValue.isBefore(newOther)) ||
      (field === "close_time" && !newOther.isBefore(newValue))
    ) {
      showSnackbar("Start time must be before end time", "error");
      setStoreHours((prev) =>
        prev.map((item) =>
          item.day === row.day ? { ...item, [field]: null } : item
        )
      );
      return;
    }

    // Prepare API payload if both times are valid and status is "Open"
    const newOpen = (field === "open_time" ? newValue : newOther).format("hh:mm A");
    const newClose = (field === "close_time" ? newValue : newOther).format("hh:mm A");
    
    const updatedStoreHours = {
      ...initialStoreHours,
      [updatedRow.day]: {
        open: newOpen,
        close: newClose,
        note: initialStoreHours[updatedRow.day]?.note || "",
      },
    };

    if (updatedRow.order_status === "Open") {
      updateStore(updatedStoreHours);
    }
  };

  const onStatusChange = (newStatus, rowData) => {
    const capitalizedStatus = newStatus?.charAt(0).toUpperCase() + newStatus.slice(1);

    setStoreHours((prev) =>
      prev.map((item) => {
        if (item.day === rowData.day) {
          const openTime = item.open_time;
          const closeTime = item.close_time;

          if (capitalizedStatus === "Open") {
            if (!openTime || !closeTime) {
              showSnackbar("Please set both start and end time before opening.", "warning");
              return item;
            }

            if (dayjs(openTime).isSameOrAfter(dayjs(closeTime))) {
              showSnackbar("Start time must be before end time.", "warning");
              return item;
            }

            const updatedObject = { ...item, order_status: capitalizedStatus };
            const updatedStoreHours = {
              ...initialStoreHours,
              [updatedObject.day]: {
                open: dayjs(updatedObject.open_time).format("hh:mm A"),
                close: dayjs(updatedObject.close_time).format("hh:mm A"),
                note: initialStoreHours[updatedObject.day]?.note || "",
              },
            };
            updateStore(updatedStoreHours);
            return updatedObject;

          } else if (capitalizedStatus === "Closed") {
            const updatedObject = { ...item, order_status: capitalizedStatus, open_time: null, close_time: null };
            const updatedStoreHours = {
              ...initialStoreHours,
              [updatedObject.day]: {
                open: null,
                close: null,
                note: initialStoreHours[updatedObject.day]?.note || "",
              },
            };
            updateStore(updatedStoreHours);
            return updatedObject;
          }
        }
        return item;
      })
    );
  };

  const updateStore = (updatedStoreHours) => {
    const payload = { store_hours: updatedStoreHours };
    axiosInstance
      .patch(`/customer/store/update-store/${storeId}`, payload)
      .then(() => fetchStore())
      .catch(() => {
        showSnackbar("Something went wrong!", "error");
      });
  };

  return (
    <div className="px-4 py-4">
      <BasicCustomTable
        columns={operationalColumns}
        rows={storeHours}
        colorHeader={true}
        customClassHeader={"table-header-cell-colored"}
        tableName="weekly-schedule"
        maxHeightClass="h-[340px]"
        handleTimeChange={handleTimeChange}
        onTagChange={onStatusChange}
        handleAccept={onAccept}
      />
    </div>
  );
}
